// ABOUTME: Recording streak analytics card component showing family behavior logging streaks and fire intensity
// ABOUTME: Displays current streak, best streak, user activity metrics, and dynamic messaging with visual fire indicators

import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { familyAPI } from '../services/api';
import { FamilyAnalyticsResponse, RecordingStreakDto } from '../types/api';
import { streakIntensityColors } from '../theme/tokens';
import naluV1Theme from '../theme/theme.nalu-v1';

interface RecordingStreakCardProps {
  familyId: number;
}

// Fire intensity mapping to visual indicators and colors
const FIRE_INTENSITY_CONFIG = {
  none: { emoji: '⭕', color: streakIntensityColors.none, label: 'No Streak' },
  small: { emoji: '🔥', color: streakIntensityColors.small, label: 'Small Fire' },
  medium: { emoji: '🔥🔥', color: streakIntensityColors.medium, label: 'Medium Fire' },
  large: { emoji: '🔥🔥🔥', color: streakIntensityColors.large, label: 'Large Fire' },
  intense: { emoji: '🔥🔥🔥🔥', color: streakIntensityColors.intense, label: 'Intense Fire' },
  blazing: { emoji: '🔥🔥🔥🔥🔥', color: streakIntensityColors.blazing, label: 'Blazing Fire' },
  inferno: { emoji: '🔥🔥🔥🔥🔥🔥', color: streakIntensityColors.inferno, label: 'Inferno' }
} as const;

type FireIntensity = keyof typeof FIRE_INTENSITY_CONFIG;

const RecordingStreakCard: React.FC<RecordingStreakCardProps> = ({ familyId }) => {
  const theme = useTheme();
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;
  const [data, setData] = useState<RecordingStreakDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStreakData = async () => {
      if (!familyId) return;

      try {
        setIsLoading(true);
        setError(null);
        
        const response = await familyAPI.getAnalytics(familyId, {
          metrics: ['recordingStreak']
        }) as FamilyAnalyticsResponse;

        setData(response.metrics.recordingStreak || null);
      } catch (err: unknown) {
        console.error('Failed to load recording streak data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load streak data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStreakData();
  }, [familyId]);

  // Loading state
  if (isLoading) {
    return (
      <Card data-testid="recording-streak-card" sx={{ minHeight: 280 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
            <CircularProgress data-testid="streak-loading" />
            <Typography sx={{ ml: 2 }}>Loading streak data...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card data-testid="recording-streak-card" sx={{ minHeight: 280 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recording Streak
          </Typography>
          <Alert severity="error" data-testid="streak-error">
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // No data state
  if (!data) {
    return (
      <Card data-testid="recording-streak-card" sx={{ minHeight: 280 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recording Streak
          </Typography>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No streak data available. Start logging behaviors to build your streak!
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const fireConfig = FIRE_INTENSITY_CONFIG[data.fireIntensity as FireIntensity] || FIRE_INTENSITY_CONFIG.none;
  const activityPercentage = Math.round((data.userActiveDaysLast7 / 7) * 100);

  return (
    <Card data-testid="recording-streak-card" sx={{ minHeight: 280 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
          Recording Streak
        </Typography>

        {/* Fire Intensity Visual */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontSize: { xs: 48, sm: 56 }, 
              mb: 1,
              filter: data.fireIntensity === 'none' ? 'grayscale(100%)' : 'none'
            }}
          >
            {fireConfig.emoji}
          </Typography>
          <Chip
            label={fireConfig.label}
            sx={{
              bgcolor: fireConfig.color,
              color: 'white',
              fontWeight: 600,
              // fontSize matches body2/button, but Chip has no Typography variant to swap to
              fontSize: '0.875rem'
            }}
            data-testid="fire-intensity-chip"
          />
        </Box>

        {/* Current Streak Display */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h3" sx={{ fontSize: { xs: 32, sm: 40 }, fontWeight: 600, color: fireConfig.color }}>
            {data.currentStreak}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            Day{data.currentStreak !== 1 ? 's' : ''} Current Streak
          </Typography>

          {/* Best Streak Display */}
          {data.showBestStreak && (
            <Typography variant="body2" color="text.secondary">
              Best: {data.bestStreak} day{data.bestStreak !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>

        {/* Dynamic Message */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography 
            variant="body1" 
            sx={{
              fontStyle: 'italic',
              fontWeight: 600,
              color: data.currentStreak > 0 ? fireConfig.color : 'text.secondary'
            }}
            data-testid="streak-message"
          >
            {data.streakMessage}
          </Typography>
        </Box>

        {/* User Activity Progress */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Your Activity (Last 7 days)
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {data.userActiveDaysLast7}/7 days
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={activityPercentage}
            sx={{
              height: 8,
              borderRadius: isNaluV1Theme ? '48px' : 4,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                bgcolor: activityPercentage >= 80 ? theme.palette.success.main :
                        activityPercentage >= 60 ? theme.palette.warning.main : theme.palette.error.main,
                borderRadius: isNaluV1Theme ? '48px' : 4
              }
            }}
            data-testid="activity-progress"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {activityPercentage}% active days
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default RecordingStreakCard;